import {
    Component,
    Event,
    EventEmitter,
    Listen,
    Prop,
    State,
    h,
} from '@stencil/core';
import { images } from '../../helpers/inline-images.service';
import { StsType } from '../../services/authentication/authentication.models';

@Component({
    tag: 'pnl-focus-header',
    styleUrl: 'pnl-focus-header.scss',
    shadow: true,
})
export class FocusHeader {
    cancelText = {
        nl_NL: 'Annuleren',
        nl_BE: 'Annuleren',
        fr_BE: 'Annuler',
        en_GB: 'Cancel',
    };

    homePageUrl = {
        'postnl.nl': 'https://www.postnl.nl',
        'postnl.be': 'https://www.postnl.be',
    };

    @State() mobileView: boolean;

    /** The domain of the client application */
    @Prop() domain: 'postnl.nl' | 'postnl.be' = 'postnl.nl';

    /** The language to show the footer in (en_GB, nl_NL, nl_BE and fr_BE) */
    @Prop() locale = 'nl_NL';

    /** Comma-separated list of available locales that the user can choose from in the pnl-language-selector.
     * For example: `nl_NL,en_GB` or `nl_BE,fr_BE,en_GB`.
     * If none or only one is specified, the language selector will not be visible  */
    @Prop() availableLocales = 'nl_NL';

    /** (optional) if set, the cancel button will not be visible */
    @Prop() hideCancel: boolean = false;

    /** (optional) if set, it will show the user-menu in focus-mode */
    @Prop() showUserMenu: boolean = false;

    /** (optional) set the type of security token service. Supported values: 'identity' or 'akamai'. */
    @Prop() stsType: StsType;

    /** (optional) The Security Token service to connect to. e.g. http://jouw.postnl.nl/identity.
     * The user menu will not be included if this is not specified.
     */
    @Prop() stsServer: string;

    /** (optional) The client id to use when requested a token. Mandatory if a sts-server is specified. */
    @Prop() clientId: string;

    /** (optional) extra audience(s) for which to request access. This will be added to the JWT token's audience, next to the default 'poa-profiles-api' */
    @Prop() audience: string;

    /** (optional) extra scope(s) to request access for. This will be added to the default OpenID scopes 'openid' and 'profile', and the scope 'poa-profiles-api' */
    @Prop() scope: string;

    /** (optional) the absolute URI to the silent-redirect.html that hosts the silent redirect script. */
    @Prop() silentRenewPage: string;

    /** (optional) this property will be added to the first element in the iframe, as 'data-silent-renew-script' attribute, so that the silentRenewPage can use that to configure which script to load. */
    @Prop() silentRenewScript: string;

    /** (optional) the relative URI to redirect users to after authenication is finished. Defaults to the root of the current origin  */
    @Prop() redirectUri: string = location.origin;

    /** (optional) the url to which the sts will redirect after logout. Defaults to the root of the current origin */
    @Prop() postLogoutRedirectUri: string = location.origin;

    /** This event will fire when the user clicks the cancel button */
    @Event() cancel: EventEmitter;

    @Listen('onLocaleChanged')
    changeLocale(locale: CustomEvent) {
        this.locale = locale.detail;
    }

    componentWillLoad() {
        const mobileMq = window.matchMedia('screen and (max-width: 57em)');
        this.mobileView = mobileMq.matches;
        mobileMq.addEventListener(
            'change',
            (mq) => (this.mobileView = mq.matches)
        );
    }

    private homeUrl() {
        return `${this.homePageUrl[this.domain]}/${this.locale.substring(
            0,
            2
        )}`;
    }

    private renderUserMenu(visible: boolean = true) {
        return this.stsServer ? (
            <pnl-user-menu
                class={'pnl-user-menu-container'}
                hidden={!visible}
                language={this.locale}
                domain={this.domain}
                sts-type={this.stsType}
                sts-server={this.stsServer}
                client-id={this.clientId}
                audience={this.audience}
                scope={this.scope}
                silentRenewPage={this.silentRenewPage}
                silentRenewScript={this.silentRenewScript}
                redirect-uri={this.redirectUri}
                post-logout-redirect-uri={this.postLogoutRedirectUri}
                menu-style="minimal"
            ></pnl-user-menu>
        ) : undefined;
    }

    render() {
        const showHeaderUtility =
            this.showUserMenu || this.availableLocales.split(',').length > 1;
        return (
            <div class="pnl-header--desktop">
                <div class="pnl-header__main pnl-header__main--desktop">
                    {showHeaderUtility && (
                        <div class="pnl-header__utility pnl-header__utility--focus">
                            <div class="pci-inner-container pci-inner-container--alt">
                                {this.renderUserMenu(this.showUserMenu)}
                                <pnl-language-selector
                                    availableLocales={this.availableLocales}
                                    locale={this.locale}
                                ></pnl-language-selector>
                            </div>
                        </div>
                    )}
                    {!showHeaderUtility && this.renderUserMenu(false)}

                    <div class="pci-inner-container pnl-navigation-container">
                        <a class="pnl-focus-link" href={this.homeUrl()}>
                            <img
                                class="pnl-navigation__link--replacement"
                                src="https://cdn.postnl.nl/images/icons/svg/logo-postnl-outline.svg"
                                alt="PostNL logo"
                            />
                        </a>
                        {!this.hideCancel && (
                            <button
                                class="pnl-header__button"
                                type="button"
                                onClick={() => this.cancel.emit()}
                            >
                                {this.cancelText[this.locale]}

                                <span
                                    class="pnl-header__button-icon"
                                    innerHTML={images.closeMenuThick}
                                ></span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
